/**
 * Adzuki. My Settings - list actions
 *
 * SET-133: the one client behaviour every settings panel that is a LIST OF RECORDS needs, in
 * one place instead of three copies.
 *
 * WHAT A LIST PANEL DOES. It is not a form, so it has no Save and no form.js behind it. Its
 * mutations are individual endpoints - make this one preferred, delete that one - and each of
 * them can answer in one of three ways:
 *
 *   confirmation_prompt   the server asking rather than acting. Raise its question in the
 *                         modal's own dialog (ground rule 6) and post again with the answer.
 *   error                 a refusal, carrying its own reason. Show the server's words.
 *   success               done. Show the server's words.
 *
 * ...and then the panel is re-read from the server, because the list is the record of what is
 * stored rather than this file's opinion of it.
 *
 * WHY IT EXISTS. SET-60 wrote this for Shipping, SET-67 copied it for Credit Cards and SET-69
 * copied it again for Franchise Documents. One copy is not duplication and two were a judgement
 * call; three is a place for a defect to hide, and one already had. See RELOAD ON EVERY
 * NON-SUCCESS below.
 *
 * WHAT THE PANELS STILL OWN. Their selectors, their endpoints and their failure strings, which
 * is the whole of my_settings_shipping.js, my_settings_financial.js and my_settings_documents.js
 * now. A fourth list declares the same and edits nothing here.
 *
 * NOTHING HERE WRITES ADVISOR-FACING COPY except the last-resort failure string each panel
 * supplies for a request that never completed. Every other message is the server's, including
 * the delete confirmations - so the wording of an irreversible-delete warning lives next to the
 * code that performs the delete, and a request that skips the dialog is asked rather than obeyed.
 */

(function MySettingsListActions($, Adzuki)
{
	"use strict";

	// EVERY SELECTOR IS SCOPED TO THE OVERLAY, and it is done here rather than asked of each
	// panel. The legacy CP|central screens these panels replace are still live and still carry
	// markup these selectors would match, so an unscoped delegated handler would fire on a page
	// that has no modal to re-render. All three copies scoped by hand and all three could have
	// forgotten; now there is one place to forget it.
	var OVERLAY = '.my-settings-overlay ';

	var list_actions = {};

	var _post,
	    _reload,
	    _notify;

	/**
	 * Re-renders a list panel from the server.
	 *
	 * keep_scroll IS TRUE: this is a RE-render of the panel the advisor is already on after an
	 * action they took on it, not navigation, so the list should not jump back to the top under
	 * them.
	 *
	 * @param  string url Panel to re-read, e.g. /MySettings/shipping
	 *
	 * @return void
	 */
	_reload = function reload(url)
	{
		if (Adzuki.MySettings && typeof Adzuki.MySettings.load === 'function') {
			Adzuki.MySettings.load(url, true);
		}
	};

	_notify = function notify(title, message)
	{
		if (typeof Adzuki.StatusMessage === 'function') {
			Adzuki.StatusMessage(title, message);
		}
	};

	/**
	 * Posts to a list endpoint and deals with the three answers it can give.
	 *
	 * @param  string url     Endpoint, without the base uri.
	 * @param  object data    Post body.
	 * @param  string failure What to say if the request itself does not complete.
	 * @param  string panel   Panel url to re-read afterwards.
	 *
	 * @return void
	 */
	_post = function post(url, data, failure, panel)
	{
		$.ajax({
			url     : (Adzuki.base_uri || '') + url,
			type    : 'POST',
			data    : data,
			dataType: 'json',
			success : function posted(response) {
				if (!response || !response.status) {
					_notify('Error', failure);

					return;
				}

				// THE SERVER ASKING RATHER THAN ACTING. No reload here and no message: the
				// advisor has not been refused, they have been asked, and the panel is still
				// accurate until they answer.
				if (response.status === 'confirmation_prompt') {
					if (Adzuki.MySettings.Save && typeof Adzuki.MySettings.Save.confirm === 'function') {
						Adzuki.MySettings.Save.confirm(response.data.message, function confirmed() {
							_post(url, $.extend({}, data, { confirmed: 1 }), failure, panel);
						});
					}

					return;
				}

				if (response.status !== 'success') {
					// THE RESPONSE'S OWN TEXT, not the generic failure. A refusal here names the
					// reason - a card used for a recent enrolment, a document Home Office has not
					// reviewed - and that reason is the whole value of the message.
					_notify('Error', (response.data ? response.data : failure));

					// RELOAD ON EVERY NON-SUCCESS, WHICH IS THE ONE BEHAVIOUR THIS EXTRACTION
					// CHANGED. The list is unchanged by a refusal but the CONTROL that was clicked
					// is not: a radio has already moved, so the panel now shows a preferred or
					// primary record that was never stored, and it stays wrong until something else
					// re-renders. my_settings_financial.js did this; my_settings_shipping.js did
					// not, and Shipping has a preferred radio too - so a refused
					// /MySettings/shippingPreferred left the wrong row selected. Unifying on the
					// safe answer fixes that. It costs a delete-only panel one redundant fetch on a
					// path the advisor rarely reaches, which is the right trade.
					_reload(panel);

					return;
				}

				_notify('Success', response.data);

				_reload(panel);
			},
			error: function failed() {
				// NO RELOAD. The request did not reach a decision - a network drop, a 500 - so
				// re-reading the panel would show the advisor a list that may be mid-anything and
				// imply the action resolved. Say it failed and leave the panel where it is.
				_notify('Error', failure);
			}
		});
	};

	/**
	 * Binds one list panel's actions.
	 *
	 * DELEGATED FROM document, because the panel arrives by AJAX and is replaced on every
	 * re-render - there is no element to bind to at ready, and re-binding after each render is
	 * how handlers get attached twice.
	 *
	 * @param  object panel An object with:
	 *
	 *                        url      the panel to re-read after an action, e.g. /MySettings/shipping
	 *                        actions  array of { event, selector, url, failure }, where
	 *                                 `selector` is relative to the overlay and `url` is a
	 *                                 function given the jQuery-wrapped element that was acted
	 *                                 on, returning the endpoint to post to.
	 *
	 * @return void
	 */
	list_actions.bind = function bind(panel)
	{
		if (!panel || !panel.url || !panel.actions) {
			return;
		}

		$.each(panel.actions, function eachAction(i, action) {
			$(document).on(action.event, OVERLAY + action.selector, function acted(e) {
				// A no-op on a change event, which is not cancelable - it is here for the click
				// handlers, whose elements would otherwise follow an href or submit a form.
				e.preventDefault();

				_post(action.url($(this)), {}, action.failure, panel.url);
			});
		});
	};

	Adzuki.MySettings             = (Adzuki.MySettings || {});
	Adzuki.MySettings.ListActions = list_actions;
}(jQuery, window.Adzuki || {}));
